import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { BankAccount, Jarticard, JarticardSobre } from '@/lib/types';
import { JARTICARD_TYPE_LABELS, JARTICARD_TYPES } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { getMyBankAccounts, buyJarticardSobre, claimDailySobre, getDailySobreStatus } from '@/lib/actions';
import { SobreReveal } from './SobreReveal';
import { BoosterPack } from './BoosterPack';
import { Coins, Loader2, PackageOpen, Gift, Clock } from 'lucide-react';

function formatCountdown(ms: number) {
  if (ms <= 0) return '00:00:00';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function remainingFor(sobre: JarticardSobre, purchases: Map<string, number>): number {
  const stock = sobre.stockPerUser || 0;
  if (stock <= 0) return Infinity;
  return Math.max(0, stock - (purchases.get(sobre.id) || 0));
}

export function SobreStore({ sobres, cardsById, purchases }: { sobres: JarticardSobre[]; cardsById: Map<string, Jarticard>; purchases: Map<string, number> }) {
  const { toast } = useToast();
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [selectedSobre, setSelectedSobre] = useState<JarticardSobre | null>(null);
  const [accountId, setAccountId] = useState<string>('');
  const [buying, setBuying] = useState(false);
  const [revealed, setRevealed] = useState<Jarticard[] | null>(null);

  // Sobre gratis diario
  const dailySobre = useMemo(() => sobres.find((s) => s.freeDaily && s.active !== false) || null, [sobres]);
  const paidSobres = useMemo(() => sobres.filter((s) => !s.freeDaily), [sobres]);
  const [dailyAvailable, setDailyAvailable] = useState(false);
  const [nextAvailableAt, setNextAvailableAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    getMyBankAccounts().then((res) => {
      if (res.success && res.accounts) {
        setAccounts(res.accounts.filter((a) => (a.accountType || 'secundaria') === 'secundaria'));
      }
      setLoadingAccounts(false);
    });
  }, []);

  useEffect(() => {
    if (!dailySobre) return;
    getDailySobreStatus(dailySobre.id).then((res) => {
      if (res.success) { setDailyAvailable(res.available); setNextAvailableAt(res.nextAvailableAt); }
    });
  }, [dailySobre?.id]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const dailyReady = dailyAvailable || (nextAvailableAt > 0 && now >= nextAvailableAt);

  const handleClaimDaily = async () => {
    if (!dailySobre) return;
    setClaiming(true);
    const res = await claimDailySobre(dailySobre.id);
    setClaiming(false);
    if (res.success && res.drawn) {
      const cards = res.drawn.map((id) => cardsById.get(id)).filter(Boolean) as Jarticard[];
      setRevealed(cards);
      setDailyAvailable(false);
      setNextAvailableAt(res.nextAvailableAt || Date.now() + 24 * 60 * 60 * 1000);
    } else {
      toast({ title: 'No disponible', description: res.message, variant: 'destructive' });
      // refresca el estado por si el cooldown sigue activo
      getDailySobreStatus(dailySobre.id).then((r) => { if (r.success) { setDailyAvailable(r.available); setNextAvailableAt(r.nextAvailableAt); } });
    }
  };

  const selectedAccount = useMemo(() => accounts.find((a) => a.id === accountId), [accounts, accountId]);

  const handleBuy = async () => {
    if (!selectedSobre || !accountId) return;
    setBuying(true);
    const res = await buyJarticardSobre(accountId, selectedSobre.id);
    setBuying(false);
    if (res.success && res.drawn) {
      const cards = res.drawn.map((id) => cardsById.get(id)).filter(Boolean) as Jarticard[];
      setRevealed(cards);
      setSelectedSobre(null);
      // refresca saldos de las cuentas tras el cobro
      getMyBankAccounts().then((r) => {
        if (r.success && r.accounts) setAccounts(r.accounts.filter((a) => (a.accountType || 'secundaria') === 'secundaria'));
      });
    } else {
      toast({ title: 'No se pudo comprar', description: res.message, variant: 'destructive' });
    }
  };

  if (sobres.length === 0) {
    return (
      <div className="grid place-items-center rounded-2xl border border-dashed border-white/10 py-20 text-center text-muted-foreground">
        <PackageOpen className="mb-3 h-10 w-10 opacity-40" />
        No hay sobres a la venta ahora mismo.
      </div>
    );
  }

  return (
    <>
      {/* Sobre gratis diario */}
      {dailySobre && (
        <div className="relative mb-6 overflow-hidden rounded-3xl border-2 border-emerald-400/40 bg-gradient-to-br from-emerald-900/40 via-card/40 to-card/20 p-5 shadow-[0_0_30px_rgba(16,185,129,0.2)]">
          <div className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-emerald-500/25 blur-3xl" />
          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="grid h-14 w-14 place-items-center rounded-2xl border border-emerald-400/40 bg-emerald-500/15 text-emerald-300"><Gift className="h-7 w-7" /></div>
              <div>
                <h3 className="font-headline text-xl font-bold">Sobre gratis diario</h3>
                <p className="text-sm text-muted-foreground">«{dailySobre.name}» — {dailySobre.numCards} carta(s) gratis cada 24 h.</p>
              </div>
            </div>
            {dailyReady ? (
              <Button onClick={handleClaimDaily} disabled={claiming} className="h-12 gap-2 bg-gradient-to-r from-emerald-500 to-green-600 px-6 font-bold text-white hover:brightness-110">
                {claiming ? <Loader2 className="h-5 w-5 animate-spin" /> : <Gift className="h-5 w-5" />} ¡Reclamar gratis!
              </Button>
            ) : (
              <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-black/40 px-5 py-3 text-center">
                <Clock className="h-5 w-5 text-emerald-300" />
                <div>
                  <p className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">Próximo en</p>
                  <p className="font-mono text-xl font-black text-emerald-300">{formatCountdown(nextAvailableAt - now)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="relative">
        {/* Ambiente de fondo */}
        <div className="pointer-events-none absolute -top-10 left-1/4 h-64 w-64 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-10 right-1/4 h-64 w-64 rounded-full bg-fuchsia-500/10 blur-3xl" />
        <div className="relative grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
          {paidSobres.map((sobre, i) => {
            const rem = remainingFor(sobre, purchases);
            const soldOut = rem <= 0;
            return (
              <BoosterPack
                key={sobre.id}
                sobre={sobre}
                delay={i * 0.05}
                soldOut={soldOut}
                stockLabel={Number.isFinite(rem) ? `Quedan ${rem}` : undefined}
                onBuy={() => { if (!soldOut) { setSelectedSobre(sobre); setRevealed(null); } }}
              />
            );
          })}
        </div>
      </div>

      {/* Diálogo de compra */}
      <Dialog open={!!selectedSobre} onOpenChange={(o) => !o && setSelectedSobre(null)}>
        <DialogContent className="max-w-md">
          {selectedSobre && (
            <>
              <DialogHeader>
                <DialogTitle>Comprar «{selectedSobre.name}»</DialogTitle>
                <DialogDescription>
                  {selectedSobre.numCards} carta(s) por {selectedSobre.price} jartis de oro. Se cobrará al instante de la cuenta elegida.
                </DialogDescription>
              </DialogHeader>

              {Number.isFinite(remainingFor(selectedSobre, purchases)) && (
                <div className="flex items-center gap-2 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-sm">
                  <span className="text-amber-300">Te quedan <b>{remainingFor(selectedSobre, purchases)}</b> de <b>{selectedSobre.stockPerUser}</b> compras de este sobre.</span>
                </div>
              )}

              {/* Probabilidades */}
              <div className="rounded-xl border border-white/8 bg-muted/30 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Probabilidades</p>
                <div className="flex flex-wrap gap-1.5">
                  {JARTICARD_TYPES.map((t) => {
                    const total = JARTICARD_TYPES.reduce((s, k) => s + (selectedSobre.probabilities?.[k] || 0), 0) || 1;
                    const pct = Math.round(((selectedSobre.probabilities?.[t] || 0) / total) * 100);
                    if (pct === 0) return null;
                    return (
                      <span key={t} className="rounded-full bg-white/5 px-2 py-0.5 text-[0.7rem]">
                        {JARTICARD_TYPE_LABELS[t]}: <b>{pct}%</b>
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Selector de cuenta */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Pagar con</label>
                {loadingAccounts ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Cargando cuentas…</div>
                ) : accounts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No tienes ninguna cuenta secundaria de JartiBank. Crea una en JartiBank para comprar sobres.</p>
                ) : (
                  <Select value={accountId} onValueChange={setAccountId}>
                    <SelectTrigger><SelectValue placeholder="Selecciona una cuenta" /></SelectTrigger>
                    <SelectContent>
                      {accounts.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.accountName} — {a.oro} oro
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selectedAccount && selectedAccount.oro < selectedSobre.price && (
                  <p className="text-xs text-rose-400">Saldo insuficiente en esta cuenta.</p>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedSobre(null)}>Cancelar</Button>
                <Button
                  onClick={handleBuy}
                  disabled={buying || !accountId || !selectedAccount || selectedAccount.oro < selectedSobre.price || remainingFor(selectedSobre, purchases) <= 0}
                >
                  {buying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />}
                  Comprar por {selectedSobre.price}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Apertura épica del sobre (revelado uno por uno) */}
      <AnimatePresence>
        {revealed && revealed.length > 0 && (
          <SobreReveal cards={revealed} onClose={() => setRevealed(null)} />
        )}
      </AnimatePresence>
    </>
  );
}
